import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ShieldCheck, ArrowDownRight } from "lucide-react";
import { PROFILE, STATS } from "@/data";

const LINES = ["MD NURUL", "ISLAM SUMON"];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });
  const glowX = useTransform(sx, (v) => v * 60);
  const glowY = useTransform(sy, (v) => v * 40);
  const orbX = useTransform(sx, (v) => v * -30);
  const orbY = useTransform(sy, (v) => v * -24);

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16"
    >
      <motion.div
        style={{ x: glowX, y: glowY }}
        className="absolute top-1/4 left-1/4 w-[42rem] h-[42rem] rounded-full bg-[#D9A441]/10 blur-[140px] pointer-events-none"
      />
      <motion.div
        style={{ x: orbX, y: orbY }}
        className="absolute bottom-0 right-0 w-[30rem] h-[30rem] rounded-full bg-[#2C4A73]/50 blur-[120px] pointer-events-none"
      />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(217,164,65,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(217,164,65,0.05)_1px,transparent_1px)] bg-[size:72px_72px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-[#D9A441] mb-8"
          data-testid="hero-overline"
        >
          Government Verified Freelancer — Bangladesh
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-[#F3EFE6]" data-testid="hero-name">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                {line}
                {i === LINES.length - 1 && <span className="text-[#D9A441]">.</span>}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 flex flex-wrap items-center gap-3"
          data-testid="hero-badge-row"
        >
          {["Digital Marketer", "Affiliate Strategist", "Web Developer"].map((b) => (
            <span key={b} className="text-xs font-mono uppercase tracking-widest text-[#C8D3E0] border border-white/15 rounded-full px-4 py-1.5 bg-white/5">
              {b}
            </span>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-[#9DAEC4] leading-relaxed"
        >
          I grow brands with performance marketing and build the websites that convert that growth into revenue.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href={PROFILE.verifyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="btn-hero-whatsapp"
            className="group flex items-center gap-2 bg-[#D9A441] hover:bg-[#E5B85C] text-[#0A1730] font-bold px-7 py-3.5 rounded-full transition-all duration-300 hover:scale-[1.03]"
          >
            <ShieldCheck size={17} />
            Verify Government ID
          </a>
          <a
            href="#contact"
            data-testid="btn-hero-contact"
            className="group flex items-center gap-2 border border-white/20 hover:border-[#D9A441]/70 text-[#F3EFE6] font-semibold px-7 py-3.5 rounded-full transition-all duration-300 hover:scale-[1.03]"
          >
            Let's Build Your Project
            <ArrowDownRight size={17} className="text-[#D9A441] transition-transform duration-300 group-hover:rotate-45" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-20 grid grid-cols-3 gap-4 max-w-2xl border-t border-white/10 pt-8"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-display text-2xl sm:text-3xl font-bold text-[#F3EFE6]">{s.value}</p>
              <p className="text-xs font-mono uppercase tracking-widest text-[#9DAEC4] mt-1">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
